import { isDefined } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';
import { ChakraProps } from '@aloft/toolkit-sui/chakra';
import { Column as ReactTableColumn } from '@tanstack/react-table';

import { useMemo, useRef } from 'react';

import { UNSET_COLUMN_SIZE } from './constants';

type Props = {
  paddingX: number;
  numColumns: number;
};

export function usePinnedColumnStyles({ paddingX, numColumns }: Props) {
  const columnStyleInfo = useRef<{ width: number | undefined; left: number }[]>([]);

  const api = useMemo(
    () => ({
      setStyleInfo(column: ReactTableColumn<any, any>) {
        if (!column.getIsPinned()) return;

        const index = column.getPinnedIndex();
        if (isDefined(columnStyleInfo.current[index])) return;

        // derive left offset from previous column's width
        const prevWidth = columnStyleInfo.current[index - 1]?.width;
        const left = isDefined(prevWidth) ? prevWidth + paddingX * 2 : 0;

        if (prevWidth === UNSET_COLUMN_SIZE) {
          console.error(
            [
              `Expected explicit size for pinned column at index ${
                index - 1
              } but got the react-table default of ${UNSET_COLUMN_SIZE}.`,
              'Ensure the column definition has defined the `size` property.',
            ].join(' '),
          );
        }

        columnStyleInfo.current[index] = { width: column.getSize(), left };
      },
      getStyles(column: ReactTableColumn<any, any>): ChakraProps {
        if (!column.getIsPinned()) return {};

        const index = column.getPinnedIndex();
        const { width, left } = columnStyleInfo.current[index] ?? {};

        return {
          width: width ? `${width}px` : undefined,
          position: 'sticky',
          left: `${left}px`,
          css: {
            [`:nth-of-type(${numColumns})`]: {
              borderRight: `2px solid`,
              borderRightColor: colors.strokeDefault,
            },
          },
        };
      },
    }),
    [numColumns, paddingX],
  );

  return api;
}
